import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { navItems, adminItems, newQuoteAction, visibleNavAction, visibleNavItems, type NavItem } from '@/app/nav';
import { activeNavPath } from './activeNavPath';
import { useSidebarSubmenu, type MenuType } from './useSidebarSubmenu';

export interface SidebarGroup {
    type: MenuType;
    heading: 'Menu' | 'Admin';
    items: NavItem[];
}

/** The permitted nav groups, the active path and the inline submenu state, shared by both sidebar layouts. */
export function useSidebarNav() {
    const { user, isInternal } = useAuth();
    const location = useLocation();

    const mainItems = useMemo(() => visibleNavItems(navItems, { user, isInternal }), [user, isInternal]);
    const otherItems = useMemo(() => visibleNavItems(adminItems, { user, isInternal }), [user, isInternal]);

    const newQuote = useMemo(() => visibleNavAction(newQuoteAction, { user }), [user]);

    const groups = useMemo<SidebarGroup[]>(
        () => [
            { type: 'main', heading: 'Menu', items: mainItems },
            ...(otherItems.length > 0 ? [{ type: 'others' as const, heading: 'Admin' as const, items: otherItems }] : []),
        ],
        [mainItems, otherItems]
    );

    const activePath = useMemo(
        () => activeNavPath([...mainItems, ...otherItems], location.pathname),
        [mainItems, otherItems, location.pathname]
    );
    const isActive = useCallback((path: string) => path === activePath, [activePath]);
    const submenu = useSidebarSubmenu(mainItems, otherItems, location.pathname);

    return { groups, isActive, submenu, newQuote };
}
